// Made-up quotations for judging the design (see lib/sample.ts). Route, customer and cargo come from the shipment with the same SRB.
import { sampleOnly } from '../../lib/sample';
import type { Tone } from '../../components/tone';
import { QUOTE_STATUSES, type Quotation, type QuoteStatus } from '@serb/shared';
import { SHIPMENTS } from '../shipments/sample';

export { QUOTE_STATUSES };
export type { PriceOption, Quotation, QuoteStatus } from '@serb/shared';

export const QUOTE_TONE: Record<QuoteStatus, Tone> = {
  Draft: 'neutral',
  Sent: 'info',
  Accepted: 'success',
  Expired: 'warning',
  Rejected: 'error',
};

/** The steps along the top of a quotation: where it is in its life */
export const QUOTE_STEPS = [
  'Draft',
  'Internal Review',
  'Send to Customer',
  'Customer Response',
  'Converted to Booking',
] as const;
const STEP_OF: Record<QuoteStatus, number> = { Draft: 0, Sent: 3, Accepted: 4, Expired: 3, Rejected: 3 };
export const quoteStep = (status: QuoteStatus) => STEP_OF[status];

type Raw = Omit<Quotation, 'client' | 'pdf'>;
const RAW: Raw[] = [
  {
    srb: 'SRB-711',
    number: 'QUOTE-26-1305-4',
    status: 'Draft',
    currency: 'USD',
    price: 1550,
    validUntil: '25 Jul 2025',
    created: '23 Jul',
    updated: '2h ago',
    carriers: 'MU / EK',
    options: [
      { service: 'MU / G9-XY', transit: '7 days', cost: 1383, price: 1550 },
      { service: 'EK', transit: '4–5 days', cost: 1480, price: 1680 },
    ],
    notes:
      'Customer requested best possible rate. Competitor price: 2,400 EUR. Target: around 2,000 EUR. Include 105 JOD trucking and clearance.',
  },
  {
    srb: 'SRB-640',
    number: 'QUOTE-26-1298-1',
    status: 'Sent',
    currency: 'EUR',
    price: 2000,
    validUntil: '25 Jul 2025',
    created: '22 Jul',
    updated: '1d ago',
    carriers: 'QR / EK',
    options: [{ service: 'QR', transit: '2 days', cost: 1720, price: 2000 }],
    notes: '',
  },
  {
    srb: 'SRB-596',
    number: 'QUOTE-26-1187-2',
    status: 'Accepted',
    currency: 'USD',
    price: 7470,
    validUntil: '20 Jun 2025',
    created: '18 Jun',
    updated: '1d ago',
    carriers: 'CMA / Turkon',
    options: [],
    notes: '',
  },
  {
    srb: 'SRB-539',
    number: 'QUOTE-26-1102-1',
    status: 'Expired',
    currency: 'USD',
    price: 4070,
    validUntil: '10 Jun 2025',
    created: '3 Jun',
    updated: '2w ago',
    carriers: 'Messina',
    options: [],
    notes: '',
  },
  {
    srb: 'SRB-703',
    number: 'QUOTE-26-1311-3',
    status: 'Sent',
    currency: 'USD',
    price: 5800,
    validUntil: '12 Aug 2025',
    created: '1 Aug',
    updated: '1d ago',
    carriers: 'Emirates',
    options: [],
    notes: '',
  },
  {
    srb: 'SRB-648',
    number: 'QUOTE-26-1292-2',
    status: 'Draft',
    currency: 'EUR',
    price: 3800,
    validUntil: '24 Jul 2025',
    created: '20 Jul',
    updated: '2d ago',
    carriers: 'RJ',
    options: [],
    notes: '',
  },
  {
    srb: 'SRB-625',
    number: 'QUOTE-26-1275-1',
    status: 'Rejected',
    currency: 'USD',
    price: 950,
    validUntil: '28 Jul 2025',
    created: '19 Jul',
    updated: '3d ago',
    carriers: 'Arkas',
    options: [],
    notes: 'Customer found a cheaper offer.',
  },
  {
    srb: 'SRB-518',
    number: 'QUOTE-26-1248-1',
    status: 'Draft',
    currency: 'USD',
    price: 3380,
    validUntil: '18 Jul 2025',
    created: '15 Jul',
    updated: '3d ago',
    carriers: 'CMA',
    options: [],
    notes: '',
  },
  {
    srb: 'SRB-701',
    number: 'QUOTE-26-1196-1',
    status: 'Accepted',
    currency: 'EUR',
    price: 2450,
    validUntil: '28 Jun 2025',
    created: '20 Jun',
    updated: '3w ago',
    carriers: 'DSV',
    options: [],
    notes: '',
  },
  {
    srb: 'SRB-512',
    number: 'QUOTE-26-0961-1',
    status: 'Accepted',
    currency: 'USD',
    price: 2100,
    validUntil: '10 Mar 2025',
    created: '6 Mar',
    updated: '4w ago',
    carriers: 'RJ',
    options: [],
    notes: '',
  },
];

export const QUOTATIONS: Quotation[] = sampleOnly(
  RAW.map((q) => ({ ...q, client: SHIPMENTS.find((x) => x.srb === q.srb)?.customer ?? '', pdf: '' })),
);

/** What is to be done next, by status. `button` is the main button on the card, if there is one. */
export const NEXT_ACTION: Record<QuoteStatus, { text: string; tone: Tone; priority: string; button?: string }> = {
  Draft: {
    text: 'Send the quotation PDF to the customer',
    tone: 'warning',
    priority: 'Normal priority',
    button: 'Send Quotation',
  },
  Sent: { text: 'Wait for the customer, follow up if no reply', tone: 'info', priority: 'Normal priority' },
  Accepted: { text: 'Convert to a booking', tone: 'success', priority: 'High priority', button: 'Convert to Booking' },
  Expired: { text: 'Renew the quotation', tone: 'warning', priority: 'Normal priority', button: 'Renew Quotation' },
  Rejected: { text: 'Review why it was declined', tone: 'neutral', priority: 'Low priority' },
};
